// @ts-nocheck
import React, { useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '../hooks';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { getSessionUserEmail } from '../config/common';
import * as licenseListAction from '../Action/License_And_Subscription/License_List_Action';
import * as licenseAdjustmentAction from '../Action/License_And_Subscription/License_Adjustment_Action';
import Loader from './Loader';

/**
 * @param {{ embedded?: boolean; onNavigateBack?: () => void }} props
 * When `embedded` (e.g. opened from Copilot), show a back control and optional `onNavigateBack`.
 */
const LicenseAndSubscription = ({ embedded, onNavigateBack }) => {
    const dispatch = useAppDispatch();
    const [licenseList, setLicenseList] = useState([]);
    const [data, setData] = useState({});
    const [errors, setErrors] = useState({});
    const [requestLicenseAdjustmentModel, setRequestLicenseAdjustmentModel] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const formRef = useRef();
    const intentRefFocus = useRef();
    const reqDateRefFocus = useRef();
    const feedbackRefFocus = useRef();
    const hasFetched = useRef();

    useEffect(() => {
        if (hasFetched.current) {
            return;
        }
        hasFetched.current = true;
        getLicensesData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getLicensesData = () => {
        setIsLoading(true);
        const param = {
            email: getSessionUserEmail(),
        };
        dispatch(licenseListAction.license_list({ data: JSON.stringify(param) }))
            .then((response) => {
                setIsLoading(false);
                if (response.status !== 1) {
                    toast.error(response.message);
                    return;
                }
                setLicenseList(response.data.licenses || []);
                setData((prev) => ({
                    ...prev,
                    intentList: response.data.intents,
                }));
            })
            .catch((error) => {
                setIsLoading(false);
                console.log(error);
                toast.error(error.message);
            });
    };

    const requestAdjustment = (e) => {
        e.preventDefault();
        const formData = new FormData(formRef.current);
        const formValues = Object.fromEntries(formData.entries());

        setErrors({});
        let customErrors = {};
        if (formValues.intent === '' || formValues.intent.trim().length === 0) {
            customErrors = { ...customErrors, intent: 'Please select intent to continue.' };
            intentRefFocus.current.focus();
        } else if (formValues.reqDate === '' || formValues.reqDate.trim().length === 0) {
            customErrors = { ...customErrors, reqDate: 'Please select requested date to continue.' };
            reqDateRefFocus.current.focus();
        } else if (formValues.feedback === '' || formValues.feedback.trim().length === 0) {
            customErrors = { ...customErrors, feedback: 'Please provide feedback to continue.' };
            feedbackRefFocus.current.focus();
        }
        if (Object.keys(customErrors).length > 0) {
            setErrors(customErrors);
            return true;
        }

        setIsLoading(true);
        const param = {
            email: getSessionUserEmail(),
            intent: formValues.intent,
            refundOrCredit: formValues.refundOrCredit && formValues.refundOrCredit === 'on',
            requestDate: formValues.reqDate,
            feedback: formValues.feedback,
        };
        dispatch(licenseAdjustmentAction.license_adjustment({ data: JSON.stringify(param) }))
            .then((response) => {
                setIsLoading(false);
                if (response.status !== 1) {
                    toast.error(response.message);
                    return;
                }
                setErrors({});
                toast.success('Adjustment request submitted successfully.');
                hideRequestLicenseAdjustment();
                getLicensesData();
            })
            .catch((error) => {
                setIsLoading(false);
                console.log(error);
                toast.error(error.message);
            });
    };

    const showRequestLicenseAdjustment = () => {
        setRequestLicenseAdjustmentModel(true);
    };

    const hideRequestLicenseAdjustment = () => {
        setRequestLicenseAdjustmentModel(false);
        if (formRef.current) formRef.current.reset();
        setErrors({});
    };

    return (
        <>
            {isLoading && <Loader />}

            <div className="container-fluid py-4 px-3 copilot-changelog">
                {embedded && typeof onNavigateBack === 'function' ? (
                    <button
                        type="button"
                        className="btn btn-outline-secondary login-form-secondary mb-3"
                        onClick={onNavigateBack}
                    >
                        <i className="fa-solid fa-arrow-left me-2" aria-hidden />
                        Back to Copilot
                    </button>
                ) : null}

                <div className="copilot-changelog-card">
                    <div className="copilot-changelog-card__accent" aria-hidden />
                    <div className="copilot-changelog-card__body">
                        <h2 className="copilot-changelog-section-title">Actions</h2>
                        <p className="copilot-changelog-section-lead mb-3">
                            Need a change to your license or billing? Start a request and we&apos;ll follow up.
                        </p>
                        <button
                            type="button"
                            className="btn btn-primary login-form-submit"
                            onClick={showRequestLicenseAdjustment}
                        >
                            <i className="fa-solid fa-sliders me-2" aria-hidden />
                            Request adjustment
                        </button>
                    </div>
                </div>

                {licenseList.length === 0 ? (
                    <div className="copilot-changelog-pending" role="status">
                        <div className="d-flex align-items-start gap-2">
                            <i className="fa-solid fa-file-signature text-primary mt-1" aria-hidden />
                            <div>
                                <p className="login-form-title h6 mb-1">No license requests yet</p>
                                <p className="small text-body-secondary mb-0">
                                    When you have active or historical license records, they will appear here.
                                </p>
                            </div>
                        </div>
                    </div>
                ) : (
                    licenseList.map((item, index) => (
                        <div key={index} className="copilot-changelog-card">
                            <div
                                className="copilot-changelog-card__accent copilot-changelog-card__accent--muted"
                                aria-hidden
                            />
                            <div className="copilot-changelog-card__body">
                                <h2 className="copilot-changelog-section-title mb-3">{item.Name}</h2>
                                <dl className="copilot-subscription-meta mb-0">
                                    <div>
                                        <dt>Request date</dt>
                                        <dd>{dayjs(item.Request_Date__c).format('YYYY-MM-DD')}</dd>
                                    </div>
                                    <div>
                                        <dt>Contact</dt>
                                        <dd>{item['Contact__r.Name']}</dd>
                                    </div>
                                    <div>
                                        <dt>Intent</dt>
                                        <dd>{item.Intent__c}</dd>
                                    </div>
                                    <div>
                                        <dt>Status</dt>
                                        <dd>
                                            <span className="badge rounded-pill text-bg-primary">
                                                {item.Status__c}
                                            </span>
                                        </dd>
                                    </div>
                                    <div>
                                        <dt>Disposition</dt>
                                        <dd>{item.Disposition__c}</dd>
                                    </div>
                                </dl>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {requestLicenseAdjustmentModel && (
                <>
                    <div
                        className="modal fade show copilot-changelog-upgrade-modal-root"
                        id="licenseAdjustmentModal"
                        role="dialog"
                        aria-labelledby="licenseAdjustmentModalLabel"
                        aria-modal="true"
                        aria-hidden="false"
                        style={{ display: 'block' }}
                    >
                        <div className="modal-dialog modal-dialog-centered mw-570 px-2" role="document">
                            <div className="modal-content copilot-changelog-upgrade-modal">
                                <div className="copilot-changelog-upgrade-modal__accent" aria-hidden />
                                <div className="modal-header copilot-changelog-upgrade-modal__header border-0">
                                    <div className="flex-grow-1 min-w-0 pe-2">
                                        <h2
                                            className="copilot-changelog-section-title copilot-changelog-upgrade-modal__title"
                                            id="licenseAdjustmentModalLabel"
                                        >
                                            Request adjustment
                                        </h2>
                                        <p className="copilot-changelog-section-lead copilot-changelog-upgrade-modal__lead mb-0 mt-1">
                                            Intent, requested date, and feedback are required. We&apos;ll follow up
                                            by email.
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn-close copilot-changelog-upgrade-modal__close flex-shrink-0 mt-0"
                                        aria-label="Close"
                                        onClick={hideRequestLicenseAdjustment}
                                    />
                                </div>
                                <form
                                    ref={formRef}
                                    id="license-adjustment-form"
                                    onSubmit={requestAdjustment}
                                >
                                    <div className="modal-body copilot-changelog-upgrade-modal__body">
                                        <div className="copilot-changelog-upgrade-modal__fields">
                                            <div className="row g-2">
                                                <div className="col-12">
                                                    <label
                                                        className="copilot-changelog-upgrade-modal__label"
                                                        htmlFor="sub-intent"
                                                    >
                                                        Intent
                                                    </label>
                                                    <select
                                                        id="sub-intent"
                                                        className="form-select form-select-sm copilot-changelog-upgrade-modal__control rounded-3"
                                                        ref={intentRefFocus}
                                                        name="intent"
                                                    >
                                                        <option value="">Select intent</option>
                                                        {data.intentList &&
                                                            data.intentList.map((opt, idx) => (
                                                                <option value={opt.value} key={idx}>
                                                                    {opt.label}
                                                                </option>
                                                            ))}
                                                    </select>
                                                    {errors?.intent ? (
                                                        <div className="invalid-feedback d-block small text-danger mt-1">
                                                            {errors.intent}
                                                        </div>
                                                    ) : null}
                                                </div>
                                                <div className="col-12">
                                                    <div className="form-check mb-0 pt-1">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            name="refundOrCredit"
                                                            id="refundOrCredit"
                                                        />
                                                        <label
                                                            className="form-check-label small"
                                                            htmlFor="refundOrCredit"
                                                        >
                                                            Refund or credit requested
                                                        </label>
                                                    </div>
                                                </div>
                                                <div className="col-12">
                                                    <label
                                                        className="copilot-changelog-upgrade-modal__label"
                                                        htmlFor="sub-req-date"
                                                    >
                                                        Requested date
                                                    </label>
                                                    <input
                                                        id="sub-req-date"
                                                        className="form-control form-control-sm login-form-control copilot-changelog-upgrade-modal__control rounded-3"
                                                        ref={reqDateRefFocus}
                                                        type="date"
                                                        name="reqDate"
                                                    />
                                                    {errors?.reqDate ? (
                                                        <div className="invalid-feedback d-block small text-danger mt-1">
                                                            {errors.reqDate}
                                                        </div>
                                                    ) : null}
                                                </div>
                                                <div className="col-12">
                                                    <label
                                                        className="copilot-changelog-upgrade-modal__label"
                                                        htmlFor="sub-feedback"
                                                    >
                                                        Feedback
                                                    </label>
                                                    <textarea
                                                        id="sub-feedback"
                                                        className="form-control form-control-sm login-form-control copilot-changelog-upgrade-modal__control copilot-changelog-upgrade-modal__textarea rounded-3"
                                                        ref={feedbackRefFocus}
                                                        rows={4}
                                                        name="feedback"
                                                        maxLength="32768"
                                                        placeholder="Context for your adjustment request…"
                                                    />
                                                    {errors?.feedback ? (
                                                        <div className="invalid-feedback d-block small text-danger mt-1">
                                                            {errors.feedback}
                                                        </div>
                                                    ) : null}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="modal-footer copilot-changelog-upgrade-modal__footer border-0 gap-2">
                                        <button
                                            className="btn btn-sm btn-outline-secondary login-form-secondary"
                                            type="button"
                                            onClick={hideRequestLicenseAdjustment}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-sm btn-primary login-form-submit"
                                        >
                                            Submit
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                    <div className="modal-backdrop fade show copilot-changelog-upgrade-modal-backdrop" />
                </>
            )}
        </>
    );
};

export default LicenseAndSubscription;
